/**
 * Least-privilege, incremental scopes (mvp-scope-and-stack §4).
 *
 * Scopes are requested PER MODULE, not all at first consent (mitigates SEC-3).
 * Each entry powers the first-run transparency screen (§4): name the scope and why.
 *
 * NOTE: Graph scope strings occasionally shift; verify at implementation against
 *       the live app registration (§4 footnote).
 */

export type CapabilityId =
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'C5'
  | 'C6'
  | 'C7'
  | 'C8'
  | 'C9'
  | 'C10'
  | 'C11'
  | 'C12'
  | 'C13'
  | 'C14'
  | 'C15';

export interface ScopeDescriptor {
  readonly capability: CapabilityId;
  readonly title: string;
  /** Graph delegated scopes this capability needs. */
  readonly scopes: readonly string[];
  /** Plain-language justification shown to the admin. */
  readonly why: string;
  readonly access: 'read' | 'write';
  /** Requested only on explicit opt-in, never at first consent. */
  readonly optInOnly?: boolean;
}

export const CAPABILITY_SCOPES: Readonly<Record<CapabilityId, ScopeDescriptor>> = {
  C1: {
    capability: 'C1',
    title: 'Connect to your tenant',
    scopes: ['User.Read'],
    why: 'Sign you in and read your own profile so the tool knows which tenant and admin it is acting for. Nothing else is read.',
    access: 'read',
  },
  C2: {
    capability: 'C2',
    title: 'Entitlement matrix',
    scopes: ['Organization.Read.All', 'Directory.Read.All'],
    why: 'Read tenant organisation and directory metadata to determine which Copilot / Agent entitlements the tenant and its users actually hold.',
    access: 'read',
  },
  C3: {
    capability: 'C3',
    title: 'Copilot adoption & seat ROI',
    scopes: ['Reports.Read.All'],
    why: 'Read aggregated usage reports to show Copilot adoption and per-seat return on the licences you are paying for.',
    access: 'read',
  },
  C4: {
    capability: 'C4',
    title: 'Oversharing & readiness score',
    scopes: ['Sites.Read.All', 'InformationProtectionPolicy.Read'],
    why: 'Scan SharePoint/OneDrive sharing and sensitivity-label posture to compute your Copilot oversharing-readiness score. Read-only.',
    access: 'read',
  },
  C5: {
    capability: 'C5',
    title: 'Agent inventory & ownership',
    scopes: ['CopilotPackages.Read.All'],
    why: 'Enumerate Copilot agents and their owners. Requires an Agent 365 licence and is enabled tenant-by-tenant; if unavailable, a Power Platform fallback is used.',
    access: 'read',
  },
  C6: {
    capability: 'C6',
    title: 'Shareable exec summary',
    scopes: [],
    why: 'Derived entirely from data already read above. No additional permission is requested.',
    access: 'read',
  },
  C7: {
    capability: 'C7',
    title: 'Flagship remediation (restrict/remove one oversharing link)',
    scopes: ['Sites.ReadWrite.All'],
    why: 'The single write capability. Broad by nature, so it is consented SEPARATELY and ONLY when you opt into remediation — never at first connect. Every write is reversible, previewed, and logged locally.',
    access: 'write',
    optInOnly: true,
  },
  C8: {
    capability: 'C8',
    title: 'Identity posture audit',
    scopes: [
      'User.Read.All',
      'Policy.Read.All',
      'RoleManagement.Read.Directory',
      'AuditLog.Read.All',
    ],
    why: 'Read users, Conditional Access policies, directory-role and PIM assignments, the MFA-registration report, and the Security Defaults policy to audit identity posture against CIS / NIST / ISO / SOC 2 / HIPAA controls. Read-only.',
    access: 'read',
  },
  C9: {
    capability: 'C9',
    title: 'Intune / device posture audit',
    scopes: [
      'DeviceManagementConfiguration.Read.All',
      'DeviceManagementManagedDevices.Read.All',
    ],
    why: 'Read device compliance policies, configuration profiles, managed devices, enrollment and endpoint-security policies to audit device posture against the same control set. Read-only.',
    access: 'read',
  },
  C10: {
    capability: 'C10',
    title: 'Applications & consent audit',
    scopes: ['Application.Read.All', 'Directory.Read.All'],
    why: 'Read app registrations (credential expiry), tenant-wide OAuth consent grants, and sign-in audiences to audit application and consent risk. Read-only.',
    access: 'read',
  },
  C11: {
    capability: 'C11',
    title: 'Licence allocation',
    scopes: ['User.Read.All', 'Organization.Read.All'],
    why: 'Read each user’s assigned licences and the tenant’s purchased SKUs and service plans to find overlapping (duplicated) service plans and redundant SKU assignments. Read-only.',
    access: 'read',
  },
  C12: {
    capability: 'C12',
    title: 'Sensitivity-label coverage',
    scopes: ['Sites.Read.All', 'InformationProtectionPolicy.Read'],
    why: 'Scan document libraries to measure the share of files carrying a sensitivity label, per site. Read-only.',
    access: 'read',
  },
  C13: {
    capability: 'C13',
    title: 'External / guest file access',
    scopes: ['Sites.Read.All', 'User.Read.All'],
    why: 'Read the guest-user roster and scan SharePoint / OneDrive item permissions to list which external users have access to which files. Read-only; the file scan is bounded and best-effort.',
    access: 'read',
  },
  C14: {
    capability: 'C14',
    title: 'Site owners inventory',
    scopes: ['Reports.Read.All'],
    why: 'List every SharePoint site in the tenant — group and non-group — with its URL and primary owner, from the SharePoint site usage report (read as JSON, so all traffic stays on graph.microsoft.com). Reports.Read.All is the delegated scope for that report. Read-only. NOTE: if your tenant conceals report data (the default), site URLs and owner names are anonymised until a Global Admin turns off “Conceal user, group, and site names in all reports”.',
    access: 'read',
  },
  C15: {
    capability: 'C15',
    title: 'Site URLs & SharePoint owners (opt-in)',
    scopes: ['Sites.FullControl.All'],
    why: 'OPTIONAL add-on to the site-owners report. With Sites.FullControl.All the signed-in admin can read every site tenant-wide, which (a) fills in each group site’s URL and (b) lists its SharePoint owner-role identities (the site’s “Owners”). Reading a site’s permissions can — per Microsoft — only be done with Sites.FullControl.All; there is no read-only scope for it. Because it is broad, tenant-wide and write-capable, it is consented SEPARATELY and ONLY when you explicitly enable this add-on — never at first connect. Used strictly read-only here; nothing is ever written to any site.',
    access: 'write',
    optInOnly: true,
  },
} as const;

/** Scopes needed just to establish the session (C1). */
export const CONNECT_SCOPES = CAPABILITY_SCOPES.C1.scopes;

export function scopesFor(cap: CapabilityId): readonly string[] {
  return CAPABILITY_SCOPES[cap].scopes;
}
